import React from 'react';
import { Chip } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat, RemoveCircleOutline } from '@mui/icons-material';
import { VarianceDirection } from '../../../../services/LaborManagementService/types/laborManagementTypes';

export type FavorDirection = 'higher' | 'lower';

interface VarianceChipProps {
  value: number | null;
  direction: VarianceDirection;
  favorDirection?: FavorDirection;
}

const icons: Record<string, React.ReactElement> = {
  up: <TrendingUp sx={{ fontSize: 16 }} />,
  down: <TrendingDown sx={{ fontSize: 16 }} />,
  flat: <TrendingFlat sx={{ fontSize: 16 }} />,
  none: <RemoveCircleOutline sx={{ fontSize: 16 }} />,
};

const favorable = { color: '#16a34a', bg: '#f0fdf4' };
const unfavorable = { color: '#dc2626', bg: '#fef2f2' };
const neutral = { color: '#2563eb', bg: '#eff6ff' };
const noData = { color: '#6b7280', bg: '#f3f4f6' };

// OverBudget = actual > budget (enum 1), UnderBudget = actual < budget (enum 2)
const OVER_KEYS = new Set(['OverBudget', '1']);
const UNDER_KEYS = new Set(['UnderBudget', '2']);
const TARGET_KEYS = new Set(['OnTarget', '0']);

function resolveStyle(direction: VarianceDirection, favorDirection: FavorDirection) {
  const key = String(direction);
  if (TARGET_KEYS.has(key)) return { ...neutral, icon: icons.flat };
  if (OVER_KEYS.has(key)) {
    const isFavorable = favorDirection === 'higher';
    return { ...(isFavorable ? favorable : unfavorable), icon: icons.up };
  }
  if (UNDER_KEYS.has(key)) {
    const isFavorable = favorDirection === 'lower';
    return { ...(isFavorable ? favorable : unfavorable), icon: icons.down };
  }
  return { ...noData, icon: icons.none };
}

const VarianceChip: React.FC<VarianceChipProps> = ({ value, direction, favorDirection = 'lower' }) => {
  const { color, bg, icon } = resolveStyle(direction, favorDirection);
  const label = value != null ? `${value >= 0 ? '+' : ''}${value.toFixed(1)}%` : '—';

  return (
    <Chip
      size="small"
      icon={icon}
      label={label}
      sx={{
        color,
        backgroundColor: bg,
        fontWeight: 600,
        fontSize: '0.75rem',
        '& .MuiChip-icon': { color },
      }}
    />
  );
};

export default VarianceChip;
